import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import AuthCallback from "@/components/AuthCallback";
import { ProtectedRoute, AdminRoute, SeniorRoute, IntelRoute, LegalRoute, CapabilityRoute } from "@/components/ProtectedRoute";
import { canSeeDemandGaps, canSeeLeadGeneration, canSeeBetaReport, canSeePreviewTools } from "@/lib/roles";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Dashboard from "@/pages/Dashboard";
import ProfilePage from "@/pages/ProfilePage";
import Matches from "@/pages/Matches";
import Directory from "@/pages/Directory";
import DealRoom from "@/pages/DealRoom";
import Onboarding from "@/pages/Onboarding";
import VerifyEmail from "@/pages/VerifyEmail";
import AcceptInvite from "@/pages/AcceptInvite";
import Pricing from "@/pages/Pricing";
import Billing from "@/pages/Billing";
import Admin from "@/pages/Admin";
import SecurityDashboard from "@/pages/SecurityDashboard";
import SystemIntelligence from "@/pages/SystemIntelligence";
import LeadGeneration from "@/pages/LeadGeneration";
import IncidentResponse from "@/pages/IncidentResponse";
import PreviewTools from "@/pages/PreviewTools";
import SecurityPage from "@/pages/SecurityPage";
import BetaReport from "@/pages/BetaReport";
import Needs from "@/pages/Needs";
import Connections from "@/pages/Connections";
import DemandGaps from "@/pages/DemandGaps";
import CohortReadiness from "@/pages/CohortReadiness";
import ReviewerWorkload from "@/pages/ReviewerWorkload";

function AppRouter() {
  const location = useLocation();
  // Emergent Google OAuth returns to /dashboard#session_id=...; process it first.
  // Also catch the MFA Google-reauth return (/security?reauth=1), including the
  // cancellation case where no #session_id fragment is present.
  if (location.hash?.includes("session_id=") ||
      new URLSearchParams(location.search).get("reauth") === "1") {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/accept-invite" element={<AcceptInvite />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/security" element={<ProtectedRoute><SecurityPage /></ProtectedRoute>} />
      <Route path="/matches" element={<ProtectedRoute><Matches /></ProtectedRoute>} />
      <Route path="/needs" element={<ProtectedRoute><Needs /></ProtectedRoute>} />
      <Route path="/connections" element={<ProtectedRoute><Connections /></ProtectedRoute>} />
      <Route path="/demand-gaps" element={<CapabilityRoute can={canSeeDemandGaps}><DemandGaps /></CapabilityRoute>} />
      <Route path="/directory" element={<ProtectedRoute><Directory /></ProtectedRoute>} />
      <Route path="/deals" element={<ProtectedRoute><DealRoom /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><Billing /></ProtectedRoute>} />
      <Route path="/admin" element={<AdminRoute><Admin /></AdminRoute>} />
      <Route path="/security-admin" element={<SeniorRoute><SecurityDashboard /></SeniorRoute>} />
      <Route path="/system-intelligence" element={<IntelRoute><SystemIntelligence /></IntelRoute>} />
      <Route path="/incident-response" element={<IntelRoute><IncidentResponse /></IntelRoute>} />
      <Route path="/lead-generation" element={<CapabilityRoute can={canSeeLeadGeneration}><LeadGeneration /></CapabilityRoute>} />
      <Route path="/cohort-readiness" element={<CapabilityRoute can={canSeeLeadGeneration}><CohortReadiness /></CapabilityRoute>} />
      <Route path="/beta-report" element={<CapabilityRoute can={canSeeBetaReport}><BetaReport /></CapabilityRoute>} />
      <Route path="/reviewer-workload" element={<LegalRoute><ReviewerWorkload /></LegalRoute>} />
      {process.env.REACT_APP_PREVIEW_TOOLS === "true" && (
        <Route path="/preview-tools" element={<CapabilityRoute can={canSeePreviewTools}><PreviewTools /></CapabilityRoute>} />
      )}
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <BrowserRouter>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
